import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
// @ts-ignore
import { Badge, Button, AvatarGroup, Avatar } from 'dsm-react-lib'
import {
  IconLock,
  IconApps,
  IconCircleArrowUpRight,
  IconDotsVertical,
  IconHourglassLow,
  IconLinkOff
} from '@tabler/icons-react'
import { MonitorIcon, WorkspaceActiveIcon } from '../Chatbot/icons/Icons'
import { DropdownMenuContainer } from '../../../styles/common'
import { getAvailabilityBadgeProps } from '../AgentsAndSkills/agentsSkillsConstants'
import {
  DropdownItem,
  DropdownToggle,
  UncontrolledDropdown,
  Card,
  CardBody,
  Spinner
} from 'reactstrap'
import { DropdownOption } from '../Applications/styles'
import RemoveAppModal from './modals/RemoveAppModal'
import ArcRequestModal from '../../_templates/ArcRequestModal'
import { useAppsService } from '../../../_services/AppsService'
import { useArcRequest } from '../../../_hooks/useArcRequest'
import { useWorkspaceService } from '../../../_services/WorkspaceService'
import { useSelector } from 'react-redux'
import { useRecentlyOpenedAppsServices } from '../../../_services/RecentlyOpenedAppsService'
import { useTrackEvent } from '@microsoft/applicationinsights-react-js'
import { appInsights } from '../../../_services/AppInsights'
import { EventType } from '../../../utility/types'
import { Tooltip } from 'react-tooltip'
import { getOwnerAvatars } from './utils'
import { useSasToken } from '../../../_hooks/useSasToken'
import { getInitials } from '../../../utility/Utils'
import { highlightSearchTerm } from '../Chatbot/utils/helpers'

interface Application {
  id: number | string
  name: string
  appIconUrl?: string
  uniqueName: string
  shortDescription?: string
  dateModified?: string
  lastModifiedBy?: string
  team?: string
  businessOwners?: {
    name: string
    date: string
    profilePictureBase64Encoded: string
  }[]
  applicationTags?: {
    id: number
    name: string
  }[]
  availabilityLabel?: string
  arc?: {
    isInProcess: boolean
    arcRoles: any[]
    hideArcRoles: boolean
  }
}

interface NewAppCardProps {
  application: Application
  id: number
  role?: string
  searchTerm?: string
  onBeforeNavigate?: () => void
}

const NewAppCard: React.FC<NewAppCardProps> = ({
  application,
  id,
  role,
  searchTerm,
  onBeforeNavigate
}) => {
  const [isArcModalOpen, setIsArcModalOpen] = useState(false)
  const { addApplication, removeApplication } = useAppsService()
  const { handleSilentArcRequest, isArcRoleLoading } = useArcRequest()
  const [selectedArcRoles, setSelectedArcRoles] = useState([])
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null)
  const [dotsHovered, setDotsHovered] = useState<boolean>(false)
  const [iconFailed, setIconFailed] = useState(false)
  const [unlinkApp, setUnlinkApp] = useState<{ isOpen: boolean; application: any }>({
    isOpen: false,
    application: null
  })
  const [localWorkspaceStatus, setLocalWorkspaceStatus] = useState<{ [key: string]: boolean }>({})
  const navigate = useNavigate()
  const availabilityBadge = getAvailabilityBadgeProps(application.availabilityLabel)
  const { updateActiveWorkspace } = useWorkspaceService()
  const { activeWorkspace } = useSelector((state: any) => state.workspaceReducer.workspaces)
  const { addRecentlyOpenedApp } = useRecentlyOpenedAppsServices()
  const appsWorkspace = useSelector((state: any) => state.appsReducer.apps)
  //@ts-ignore
  const trackAddToWorkspace = useTrackEvent(appInsights, EventType.AddToWorkspace)
  const { appendToken, hasToken, isExpired } = useSasToken()
  const location = useLocation()

  // Retry loading the icon once a valid SAS token becomes available — the first render can
  // request the image before the token is ready, causing a false-positive onError that would
  // otherwise permanently lock in the fallback icon for this card.
  useEffect(() => {
    setIconFailed(false)
  }, [application?.appIconUrl, hasToken, isExpired])

  const onArcModalAccess = () => {
    setIsArcModalOpen((prevState: any) => !prevState)
  }

  const onSilentAccess = async (selectedArcRoles: any) => {
    await handleSilentArcRequest({
      application,
      selectedArcRoles
    })
  }

  const routeToWorkspace = (workspaceId?: any) => {
    updateActiveWorkspace(workspaceId !== 'my-workspace' ? workspaceId : 'my-workspace')
    navigate(`/workspace?activeApp=${application.uniqueName}`)
  }

  const onAddApp = async (app: any) => {
    const appId = app.id || app.applicationId
    setLocalWorkspaceStatus((prev) => ({ ...prev, [appId]: true }))
    try {
      trackAddToWorkspace({ appAdd: JSON.stringify(app) })
      await addApplication(app)
    } catch (error) {
      console.error('Failed to add app:', error)
      setLocalWorkspaceStatus((prev) => ({ ...prev, [appId]: undefined }))
    }
  }

  const onRemoveApp = async (app: any) => {
    const appId = app.id || app.applicationId
    setLocalWorkspaceStatus((prev) => ({ ...prev, [appId]: false }))
    try {
      await removeApplication(app)
    } catch (error) {
      console.error('Failed to remove app:', error)
      setLocalWorkspaceStatus((prev) => ({ ...prev, [appId]: undefined }))
    }
  }

  return (
    <Card
      key={id}
      className="!mb-1 pt-2 pb-0 !min-h-[360px] h-auto !min-w-[380px] !border-none flex-shrink-0 cursor-pointer transition-all duration-300 relative overflow-visible card-bg-transparent"
      onClick={() => {
        if (onBeforeNavigate) {
          onBeforeNavigate()
        }
        navigate(`/apps/${application.uniqueName}`)
      }}>
      <CardBody className="!px-0 !pt-0 !pb-0 flex items-start relative overflow-visible">
        <div
          className={`relative w-[360px] min-h-[304px] rounded-lg overflow-visible transition-border theme-card-border theme-surface ${
            hoveredIdx === id ? ' theme-card-border-hover' : ''
          }`}
          onMouseEnter={() => setHoveredIdx(id)}
          onMouseLeave={() => setHoveredIdx(null)}>
          <div
            className={`theme-card-icon-border theme-surface-muted rounded-xl w-24 h-24 px-2 py-1 pointer-events-none absolute z-2 left-6 -top-12 flex items-center justify-center`}>
            <span className="flex items-center justify-center w-full h-full">
              {application?.appIconUrl && !iconFailed ? (
                <img
                  src={appendToken(application?.appIconUrl)}
                  alt={application?.name}
                  className="!min-w-24 h-24 !rounded-[10px]"
                  onError={() => setIconFailed(true)}
                />
              ) : (
                <IconApps color="var(--icon-color-200)" size={40} />
              )}
            </span>
          </div>
          <div className="w-full mt-4 p-2 relative">
            <div className="flex items-center gap-1">
              {/* -webkit-box (line-clamp) misbehaves as a direct flex item, so wrap it in a plain block */}
              <div className="min-w-0">
                <h4
                  className="font-bold text-dark text-[20px] line-clamp-1"
                  title={application?.name}>
                  {searchTerm && searchTerm.trim()
                    ? highlightSearchTerm(application?.name || '', searchTerm)
                    : application?.name}
                </h4>
              </div>
              {availabilityBadge && (
                <Badge
                  type={availabilityBadge.type}
                  variant={availabilityBadge.variant}
                  color={availabilityBadge.color}
                  label={availabilityBadge.label}
                  className={`shrink-0 z-3 ${availabilityBadge.className}`}
                />
              )}
            </div>
            <p className="mb-0 text-gray-500 text-[14px] word-break line-clamp-2 ellipsis min-h-[40px]">
              {application?.shortDescription}
            </p>
            <div className="absolute -top-10 right-4 flex items-center space-x-1">
              {application?.arc?.isInProcess && (
                <span>
                  <Button
                    className="py-1/2 px-1 text-[12px] mr-1 theme-warning-light-btn"
                    color="warning"
                    label="Pending"
                    icon={<IconHourglassLow size={12} />}
                    variant="outline"
                    size="small"
                    disabled
                  />
                </span>
              )}
              {(application?.arc?.arcRoles?.length ?? 0) > 0 && !application?.arc?.isInProcess && (
                <span>
                  <Button
                    className="py-1/2 px-1 text-[12px] mr-1 theme-accent-outline-btn"
                    color="primary"
                    label={!isArcRoleLoading ? 'Request' : 'Requesting'}
                    icon={
                      !isArcRoleLoading ? (
                        <IconLock size={12} />
                      ) : (
                        <Spinner color="var(--color-primary)" size="sm" />
                      )
                    }
                    variant="outline"
                    size="small"
                    onClick={(e: any) => {
                      e.preventDefault()
                      e.stopPropagation()
                      if (application?.arc?.hideArcRoles) {
                        onSilentAccess(application?.arc?.arcRoles || [])
                      } else {
                        onArcModalAccess()
                      }
                    }}
                  />
                </span>
              )}
              {!application?.arc?.isInProcess && application?.arc?.arcRoles?.length === 0 && (
                <Button
                  className="py-1/2 px-1 text-[12px] mr-1"
                  color="primary"
                  icon={<IconCircleArrowUpRight size={20} color="white" />}
                  label="Launch "
                  variant="fill"
                  size="small"
                  onClick={(e: any) => {
                    e.preventDefault()
                    e.stopPropagation()
                    addRecentlyOpenedApp(application)
                    routeToWorkspace(activeWorkspace)
                    if (onBeforeNavigate) {
                      onBeforeNavigate()
                    }
                  }}
                />
              )}
              {(() => {
                // Check local state first, then fall back to Redux state
                const appId = application.id
                const isInWorkspace =
                  localWorkspaceStatus[appId] !== undefined
                    ? localWorkspaceStatus[appId]
                    : appsWorkspace?.some(
                        (workspaceApp: any) =>
                          workspaceApp.id === appId || workspaceApp.applicationId === appId
                      )

                return isInWorkspace ? (
                  <span
                    className="cursor-pointer pl-1 ml-50"
                    onClick={(e) => {
                      e.stopPropagation()
                      onRemoveApp(application)
                    }}>
                    <span
                      data-tooltip-id={`remove-from-workspace-${application?.id}`}
                      data-tooltip-content="Remove from workspace"
                      className="inline-flex">
                      <WorkspaceActiveIcon size={25} />
                    </span>
                    <Tooltip
                      id={`remove-from-workspace-${application?.id}`}
                      place="top"
                      className="!z-[5]"
                    />
                  </span>
                ) : (
                  <span
                    className="cursor-pointer pl-1 ml-50"
                    onClick={(e) => {
                      e.stopPropagation()
                      onAddApp(application)
                    }}>
                    <span
                      data-tooltip-id={`add-to-workspace-${application?.id}`}
                      data-tooltip-content="Add to workspace"
                      className="inline-flex">
                      <MonitorIcon size={25} />
                    </span>
                    <Tooltip
                      id={`add-to-workspace-${application?.id}`}
                      place="top"
                      className="!z-[5]"
                    />
                  </span>
                )
              })()}
              {role !== 'Member' && location.pathname.includes('/team') && (
                <UncontrolledDropdown direction="start">
                  <DropdownToggle
                    tag="span"
                    onMouseEnter={() => setDotsHovered(true)}
                    onMouseLeave={() => setDotsHovered(false)}
                    onClick={(e) => e.stopPropagation()}>
                    <IconDotsVertical
                      size={24}
                      color={dotsHovered ? 'var(--color-primary)' : 'var(--icon-color-200)'}
                    />
                  </DropdownToggle>
                  <DropdownMenuContainer $positionFixed={true} className="!right-0 !left-auto">
                    <DropdownItem
                      className="w-full theme-dropdown-danger-item"
                      onClick={(e) => {
                        e.stopPropagation()
                        setUnlinkApp({ isOpen: true, application })
                      }}>
                      <DropdownOption className="flex items-center">
                        <IconLinkOff size={15} />
                        <span className="align-middle ml-1">Remove</span>
                      </DropdownOption>
                    </DropdownItem>
                  </DropdownMenuContainer>
                </UncontrolledDropdown>
              )}
            </div>
          </div>
          <div className="mt-auto theme-border-default h-[1px] mx-2 border-t" />
          <div className="flex flex-col p-2 pb-1 w-full rounded-b-lg">
            <div className="grid grid-cols-2 gap-x-8 gap-y-5">
              <div className="flex flex-col items-start mb-1">
                <div className="block w-[100%]">
                  <p className="section-label mb-50">OWNERS</p>
                  <div className="flex items-center">
                    {application?.businessOwners && application?.businessOwners?.length > 1 && (
                      <AvatarGroup avatars={getOwnerAvatars(application?.businessOwners)} />
                    )}
                    {application?.businessOwners?.length === 1 && (
                      <div className="mt-[5px]">
                        <div className="flex items-start">
                          <Avatar
                            content={
                              application?.businessOwners?.[0]?.profilePictureBase64Encoded ||
                              getInitials(application?.businessOwners?.[0]?.name)
                            }
                            size="32px"
                            className="!ml-0 mt-0 !min-w-[32px] dsm-avatar-brand"
                            alt={application?.businessOwners?.[0]?.name}
                          />
                          <div className="flex flex-col mb-[5px]">
                            <span
                              className="text-base font-bold theme-text-heading truncate max-w-[120px]"
                              title={application?.businessOwners?.[0]?.name}>
                              {application?.businessOwners?.[0]?.name?.length > 8
                                ? `${application?.businessOwners?.[0]?.name?.slice(0, 8)}…`
                                : application?.businessOwners?.[0]?.name}
                            </span>
                            <p className="caption-text line-clamp-1 m-0">{application?.team}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-start">
                <p className="section-label mb-[5px]">DATE MODIFIED</p>
                <div>
                  <span className="text-[16px] theme-text-heading">
                    {application?.dateModified &&
                      new Date(application.dateModified).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })}
                  </span>
                  {application?.lastModifiedBy && (
                    <>
                      <p
                        className="caption-text ms-0 line-clamp-1"
                        data-tooltip-id={`modified-by-${application?.id}`}
                        data-tooltip-content={application?.lastModifiedBy}>
                        by {application?.lastModifiedBy}
                      </p>
                      <Tooltip
                        id={`modified-by-${application?.id}`}
                        place="top"
                        className="!z-[5]"
                      />
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="block gap-x-4 gap-y-2 w-full">
              <p className="section-label mb-50">TAGS</p>
              <div className="flex items-start flex-nowrap w-[280px] justify-start space-x-2">
                {(() => {
                  const MAX_TAGS = 2
                  const visibleTags = application?.applicationTags?.slice(0, MAX_TAGS)
                  const hiddenTags =
                    (application?.applicationTags?.length ?? 0) > MAX_TAGS
                      ? application?.applicationTags?.slice(MAX_TAGS)
                      : []
                  return (
                    <>
                      {visibleTags?.map((tag: any, index: number) => (
                        <Badge
                          type="pill"
                          key={tag.id || index}
                          label={tag?.name || tag?.tag?.name}
                          className={
                            'theme-tag-neutral !px-3 !py-2 !font-medium !whitespace-nowrap !overflow-hidden !text-ellipsis'
                          }
                        />
                      ))}
                      {(hiddenTags?.length ?? 0) > 0 && (
                        <Badge
                          type="pill"
                          label={`+${hiddenTags?.length}`}
                          className="theme-badge-primary-soft"
                        />
                      )}
                    </>
                  )
                })()}
              </div>
            </div>
          </div>
        </div>
      </CardBody>
      {isArcModalOpen && (
        <ArcRequestModal
          isOpen={isArcModalOpen}
          toggle={onArcModalAccess}
          arcRoles={application?.arc?.arcRoles}
          handleOnRequest={onSilentAccess}
          setSelectedArcRoles={setSelectedArcRoles}
          selectedArcRoles={selectedArcRoles}
        />
      )}
      {unlinkApp.isOpen && (
        <RemoveAppModal
          isOpen={unlinkApp.isOpen}
          toggle={() => setUnlinkApp({ isOpen: false, application: null })}
          app={unlinkApp.application}
        />
      )}
    </Card>
  )
}
export default NewAppCard
